class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    // Get cart items on page load
    this.getCartItems();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    // Only make #cart-icon-bubble a modal trigger when we are not on the cart page
    if(typeof cartLink.dataset.cartPage !== 'string' || cartLink.dataset.cartPage !== 'true') {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink)
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer-inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer-inner') || this.querySelector('.drawer-close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    /**
     * GA4 DataLayer Event: View Cart
     * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#view_cart
     */
     try {
      if(Array.isArray(this.cartItems)) {
        const items = this.cartItems.map(product => {
          const {product_id: item_id, variant_id: item_variant, title: item_name, tags, type: item_category, quantity} = product;
          return {
            item_id,
            item_name,
            item_variant,
            item_category,
            quantity,
            ...calculatePrice({
              product,
              product_tags: Array.isArray(tags) ? tags : []
            })
          };
        });
        pushDataLayerEvent({
          event: 'view_cart',
          ecommerce: {
            currency: this.currency,
            value: calculateValue(items),
            items: items
          }
        });
      }
    } catch (error) {
      console.log('GA4: view_cart', error);
    }

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer-inner').classList.contains('is-empty') && this.querySelector('.drawer-inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML = 
        this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    // Update cart items data on this cart-drawer instance
    this.getCartItems();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /**
   * Pulls cart items from a script tag that must be nested inside of the web component.
   * @returns {Array} Shopify cart.items array of line items
   * @see https://shopify.dev/docs/api/liquid/objects/cart#cart-items
   */
   getCartItems() {
    const jsonCartItems = this.querySelector('#json-cart-data[type="application/json"]');
    if(jsonCartItems !== null) {
      const {cart_items, cart_products, cart_variants, currency} = JSON.parse(jsonCartItems.textContent);
      this.cartItems = cart_items.map((product, index) => {
        const {title, type, tags} = cart_products[index];
        const {compare_at_price} = cart_variants[index];
        return {
          ...product,
          compare_at_price: !Number.isNaN(compare_at_price) ? compare_at_price : null,
          type,
          tags,
          title
        }
      });
      this.currency = currency;
      // Remove the <script> tag we used to load the JSON from the DOM
      jsonCartItems.parentNode.removeChild(jsonCartItems);
    }
    return Array.isArray(this.cartItems) ? this.cartItems : [];
  }
  getCurrency() {
    return this.currency;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.cartType = 'cart-drawer';
  }
  
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer-inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
